import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { CONFIG, normalizeInviteCode } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { DesktopColumns } from '../../components/ui/DesktopColumns';
import { Avatar, MiniLeagueMark } from '../../components/ui/bits';
import { ArrowRight, Check } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import { useJoinMiniLeague, useMyMemberships, usePreviewMiniLeague } from '../../hooks/useQueries';
import { ModalNav } from './parts';
import { cn } from '../../lib/cn';
import type { MiniLeagueNavState } from './miniLeagueHelpers';

const CODE_LEN = CONFIG.DUEL.INVITE_CODE_LENGTH;

export default function Join() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as MiniLeagueNavState | null;
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState(() => normalizeInviteCode(params.get('code') ?? '').slice(0, CODE_LEN));
  const preview = usePreviewMiniLeague();
  const join = useJoinMiniLeague(uid);
  const { data: memberships } = useMyMemberships(uid);

  useEffect(() => {
    if (code.length === CODE_LEN) {
      preview.mutate(code);
    } else {
      preview.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const found = preview.data?.ok ? preview.data.preview : null;
  const notFound = preview.isError && code.length === CODE_LEN;
  const inBaseLeague = useMemo(
    () => (found ? (memberships ?? []).some((m) => m.leagueId === found.leagueId) : false),
    [found, memberships]
  );
  const needsBaseLeague = !!found && !found.alreadyMember && !inBaseLeague;
  const canJoin = !!found && (found.alreadyMember || inBaseLeague);

  const onJoin = () => {
    if (!found || join.isPending) return;
    if (found.alreadyMember) {
      navigate(`/leagues/${found.miniLeagueId}`, { state: navState });
      return;
    }
    if (!inBaseLeague) return;
    impact('success');
    join.mutate(code, {
      onSuccess: (res) =>
        navigate(`/leagues/${res.miniLeague.miniLeagueId}`, { replace: true, state: navState })
    });
  };

  return (
    <AppShell>
      <Content>
        <ModalNav title="Join a league" onCancel={() => navigate('/leagues', { state: navState })} />

        <DesktopColumns
          asideWidth="380px"
          stickyAside={false}
          main={
            <>
        <div className="pt-5 text-center md:text-left">
          <h1 className="text-[25px] font-extrabold leading-[1.05] tracking-title text-ink">Enter your code</h1>
          <p className="mx-auto mt-2 max-w-[280px] text-[13px] font-medium leading-[1.4] text-ink2 md:mx-0">
            Ask whoever created the table for the {CODE_LEN}-character code — or paste it from a shared link.
          </p>
        </div>

        <div className="relative pt-6">
          <div className="flex gap-2">
            {Array.from({ length: CODE_LEN }).map((_, i) => {
              const ch = code[i];
              const active = i === code.length;
              return (
                <div
                  key={i}
                  className={cn(
                    'num grid aspect-[1/1.15] flex-1 place-items-center rounded-[14px] text-[26px] font-bold text-ink',
                    ch
                      ? 'border-[1.5px] border-ink bg-card'
                      : active
                        ? 'border-[1.5px] border-ink bg-card'
                        : 'border-[1.5px] border-ink4 bg-fill'
                  )}
                >
                  {ch ?? (active ? <span className="h-[26px] w-[2px] animate-pulse rounded-full bg-accent" /> : '')}
                </div>
              );
            })}
          </div>
          <input
            autoFocus
            value={code}
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={CODE_LEN}
            onChange={(e) => setCode(normalizeInviteCode(e.target.value).slice(0, CODE_LEN))}
            className="absolute inset-x-0 bottom-0 top-6 w-full cursor-pointer opacity-0"
            aria-label="Invite code"
          />
        </div>
            </>
          }
          aside={
            <>
        {found && (
          <div className="pt-6 md:pt-0">
            <div className="flex items-center gap-1.5 px-1 pb-2.5">
              <Check width={14} height={14} strokeWidth={3} className="text-accent-ink" />
              <span className="text-[12px] font-bold text-accent-ink">Code found</span>
            </div>
            <div className="rounded-[22px] border-[1.5px] border-accent bg-card p-4 shadow-[0_14px_30px_-16px_rgba(10,150,110,0.3)]">
              <div className="flex items-center gap-[13px]">
                <MiniLeagueMark icon={found.icon} color={found.color} size={46} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[17px] font-extrabold tracking-tight text-ink">{found.name}</div>
                  <div className="truncate text-[12px] font-medium text-ink2">
                    {found.memberCount} {found.memberCount === 1 ? 'member' : 'members'} · {found.leagueName}
                  </div>
                </div>
              </div>
              <div className="mt-3.5 flex items-center gap-2.5 border-t border-black/6 pt-3.5">
                <Avatar name={found.ownerName} tone={2} size={28} />
                <span className="text-[12px] font-medium text-ink2">
                  Run by <span className="font-bold text-ink">{found.ownerName}</span>
                </span>
              </div>
            </div>
          </div>
        )}

        {needsBaseLeague && (
          <div className="pt-4">
            <div className="rounded-[16px] border border-[#E8C547]/40 bg-[#FFF9E6] p-4">
              <div className="text-[13px] font-extrabold tracking-snug text-[#8A6B00]">Join {found!.leagueName} first</div>
              <p className="mt-1 text-[12px] font-medium leading-[1.45] text-[#8A6B00]/90">
                Mini leagues track the picks you make in the base competition. Play {found!.leagueName} once, then come
                back here to join the table.
              </p>
              <button
                type="button"
                onClick={() => {
                  impact('medium');
                  navigate(`/play/${found!.leagueId}`);
                }}
                className="tap mt-3 flex w-full items-center justify-center gap-2 rounded-[13px] bg-night py-3 text-[14px] font-extrabold text-white"
              >
                Go to {found!.leagueName}
                <ArrowRight width={15} height={15} strokeWidth={2.6} className="text-white" />
              </button>
            </div>
          </div>
        )}

        {notFound && (
          <p className="pt-6 text-center text-[13px] font-semibold text-danger">No mini league matches that code.</p>
        )}

        {join.isError && (
          <p className="pt-4 text-center text-[13px] font-semibold text-danger">
            Couldn’t join — make sure you’ve joined {found?.leagueName ?? 'the base league'} first.
          </p>
        )}
            </>
          }
        />

        <div className="pt-8">
          <button
            type="button"
            disabled={!canJoin || join.isPending}
            onClick={onJoin}
            className="tap w-full rounded-[17px] bg-gradient-to-br from-accent to-accent-deep py-[17px] text-center text-[16px] font-extrabold tracking-snug text-white shadow-featured disabled:opacity-50"
          >
            {join.isPending
              ? 'Joining…'
              : found
                ? found.alreadyMember
                  ? `Open ${found.name}`
                  : needsBaseLeague
                    ? `Join ${found.leagueName} first`
                    : `Join ${found.name}`
                : 'Join'}
          </button>
        </div>
      </Content>
    </AppShell>
  );
}
